(() => {
  const SIZE_ADVISOR_DEBUG = false;
  const STANDARD_CARD_LONG_MM = 85.6;
  const STANDARD_CARD_SHORT_MM = 53.98;
  const PALM_WIDTH_TO_CIRCUMFERENCE_FACTOR = 2.15;
  const VISIBLE_HANDLE_RADIUS = 5;
  const HANDLE_HIT_RADIUS = 16;
  const LINE_HIT_RADIUS = 10;
  const MIN_CARD_SIDE_PX = 36;
  const MIN_PALM_LINE_PX = 40;
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;

  const gloveSizes = [
    { size: '6½', cm: 16.5 },
    { size: '7', cm: 18 },
    { size: '7½', cm: 19 },
    { size: '8', cm: 20.5 },
    { size: '8½', cm: 22 },
    { size: '9', cm: 23.5 },
    { size: '9½', cm: 25 },
    { size: '10', cm: 26.5 },
  ];

  const SAMPLE_FILES = {
    photo: 'concierge-real-photo-crop.png',
    '1': 'concierge-sample-hand.svg',
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const normalizeSizeLabel = (value = '') => {
    return String(value)
      .trim()
      .replace(/\s+/g, '')
      .replace(/,/g, '.')
      .replace(/½/g, '.5')
      .replace(/[^\d.]/g, '');
  };

  const debugLog = (...args) => {
    if (!SIZE_ADVISOR_DEBUG) return;
    console.log('[SizeAdvisor]', ...args);
  };

  const fitTypeForCalculator = (fitGuidance = '') => {
    const normalized = String(fitGuidance).toLowerCase();
    if (normalized.includes('close')) return 'Slim';
    if (normalized.includes('easy')) return 'Comfort';
    return 'Regular';
  };

  const recommendSize = (circumferenceCm, fitType = 'Regular') => {
    const sorted = [...gloveSizes].sort((a, b) => a.cm - b.cm);
    const nearest = sorted.reduce((best, option) => {
      if (!best) return option;
      return Math.abs(option.cm - circumferenceCm) < Math.abs(best.cm - circumferenceCm) ? option : best;
    }, null);

    let lower = sorted[0];
    let upper = sorted[sorted.length - 1];
    for (let index = 0; index < sorted.length - 1; index += 1) {
      if (circumferenceCm >= sorted[index].cm && circumferenceCm <= sorted[index + 1].cm) {
        lower = sorted[index];
        upper = sorted[index + 1];
        break;
      }
    }

    const midpoint = (lower.cm + upper.cm) / 2;
    const betweenSizes = lower !== upper && Math.abs(circumferenceCm - midpoint) <= 0.2;
    let recommended = nearest;
    let message = 'This is an estimate based on your photo. For the most accurate result, measure your palm with a tape.';

    if (betweenSizes) {
      if (fitType === 'Slim') {
        recommended = upper;
        message = `You are between sizes. This glove has a neater fit, so we recommend ${upper.size}.`;
      } else if (fitType === 'Comfort') {
        recommended = nearest;
        message = `You are between sizes. This glove carries a slightly easier fit, so ${nearest.size} is the safer first choice.`;
      } else {
        recommended = upper;
        message = `You are between sizes. Choose ${upper.size} for a more comfortable fit.`;
      }
    } else if (fitType === 'Slim') {
      const boundaryDistance = upper.cm - circumferenceCm;
      if (upper !== lower && boundaryDistance >= 0 && boundaryDistance < 0.2) {
        recommended = upper;
        message = `This glove is cut neatly on the hand, so we recommend ${upper.size} as your safer first size.`;
      }
    }

    return {
      recommendedSize: recommended.size,
      estimatedCm: circumferenceCm,
      estimatedIn: circumferenceCm / 2.54,
      betweenSizes,
      lowerSize: betweenSizes ? lower.size : undefined,
      upperSize: betweenSizes ? upper.size : undefined,
      message,
    };
  };

  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  const pointInRect = (point, rect) => {
    const left = Math.min(rect.x, rect.x + rect.w);
    const top = Math.min(rect.y, rect.y + rect.h);
    const right = Math.max(rect.x, rect.x + rect.w);
    const bottom = Math.max(rect.y, rect.y + rect.h);
    return point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
  };

  const segmentDistance = (point, a, b) => {
    const l2 = Math.pow(distance(a, b), 2);
    if (!l2) return distance(point, a);
    let t = ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / l2;
    t = clamp(t, 0, 1);
    const projection = {
      x: a.x + t * (b.x - a.x),
      y: a.y + t * (b.y - a.y),
    };
    return distance(point, projection);
  };

  const getEventPoint = (event, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const normalizedRect = (rect) => ({
    x: Math.min(rect.x, rect.x + rect.w),
    y: Math.min(rect.y, rect.y + rect.h),
    w: Math.abs(rect.w),
    h: Math.abs(rect.h),
  });

  const createInitialCardRect = () => ({
    x: 0.66,
    y: 0.18,
    w: 0.2,
    h: 0.2 * (STANDARD_CARD_SHORT_MM / STANDARD_CARD_LONG_MM),
  });

  const createInitialPalmLine = () => ({
    x1: 0.22,
    y1: 0.56,
    x2: 0.68,
    y2: 0.56,
  });

  const cardHandles = (card) => {
    const rect = normalizedRect(card);
    return {
      nw: { x: rect.x, y: rect.y },
      ne: { x: rect.x + rect.w, y: rect.y },
      se: { x: rect.x + rect.w, y: rect.y + rect.h },
      sw: { x: rect.x, y: rect.y + rect.h },
    };
  };

  const drawCardHandle = (context, point) => {
    context.beginPath();
    context.fillStyle = '#ffffff';
    context.strokeStyle = '#111111';
    context.lineWidth = 2;
    context.arc(point.x, point.y, VISIBLE_HANDLE_RADIUS, 0, Math.PI * 2);
    context.fill();
    context.stroke();
  };

  const drawPalmCap = (context, point, angle) => {
    const capHalf = 8;
    const dx = Math.cos(angle + Math.PI / 2) * capHalf;
    const dy = Math.sin(angle + Math.PI / 2) * capHalf;
    context.beginPath();
    context.moveTo(point.x - dx, point.y - dy);
    context.lineTo(point.x + dx, point.y + dy);
    context.lineWidth = 3;
    context.strokeStyle = '#ffffff';
    context.stroke();
    context.beginPath();
    context.moveTo(point.x - dx, point.y - dy);
    context.lineTo(point.x + dx, point.y + dy);
    context.lineWidth = 1.5;
    context.strokeStyle = '#111111';
    context.stroke();
  };

  const selectVariantSize = (root, sizeLabel) => {
    const normalizedTarget = normalizeSizeLabel(sizeLabel);
    const productScope = root.closest('product-price') || document;
    const sizeFieldsets = Array.from(productScope.querySelectorAll('.variant-option')).filter((option) => {
      const title = option.querySelector('.variant-option__legend-title, .variant-option__dropdown-header label');
      return /size|taglia/i.test(title?.textContent || '');
    });

    for (const fieldset of sizeFieldsets) {
      const radioInputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      for (const input of radioInputs) {
        const candidate = normalizeSizeLabel(input.value || input.getAttribute('aria-label') || '');
        if (candidate === normalizedTarget) {
          input.checked = true;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          debugLog('selected variant', input.value);
          return input.value;
        }
      }

      const select = fieldset.querySelector('select');
      if (select) {
        const option = Array.from(select.options).find((item) => normalizeSizeLabel(item.value || item.textContent || '') === normalizedTarget);
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
          debugLog('selected variant', option.value);
          return option.value;
        }
      }
    }

    debugLog('selected variant', 'not found');
    return null;
  };

  const initSizeAdvisor = (root) => {
    if (!root || root.dataset.sizeAdvisorBound === 'true') return;
    root.dataset.sizeAdvisorBound = 'true';

    const fitGuidance = root.dataset.fitGuidance || 'Regular fit';
    const fitType = fitTypeForCalculator(fitGuidance);
    const dialogComponent = root.closest('dialog-component');
    const canvas = root.querySelector('[data-size-advisor-canvas]');
    const context = canvas?.getContext('2d');
    const uploadInput = root.querySelector('[data-size-advisor-input]');
    const chartTab = root.querySelector('[data-size-advisor-tab="chart"]');
    const findTab = root.querySelector('[data-size-advisor-tab="find"]');
    const chartPanel = root.querySelector('[data-size-advisor-panel="chart"]');
    const findPanel = root.querySelector('[data-size-advisor-panel="find"]');
    const uploadButton = root.querySelector('[data-size-advisor-upload]');
    const confirmCardButton = root.querySelector('[data-size-advisor-confirm-card]');
    const backToCardButton = root.querySelector('[data-size-advisor-back-card]');
    const calculateButton = root.querySelector('[data-size-advisor-calculate]');
    const resetButton = root.querySelector('[data-size-advisor-reset]');
    const canvasShell = root.querySelector('[data-size-advisor-canvas-shell]');
    const precisionTools = root.querySelector('[data-size-advisor-precision]');
    const zoomOutButton = root.querySelector('[data-size-advisor-zoom-out]');
    const zoomInButton = root.querySelector('[data-size-advisor-zoom-in]');
    const zoomResetButton = root.querySelector('[data-size-advisor-zoom-reset]');
    const zoomDisplay = root.querySelector('[data-size-advisor-zoom-display]');
    const promptTitle = root.querySelector('[data-size-advisor-prompt-title]');
    const promptCopy = root.querySelector('[data-size-advisor-prompt-copy]');
    const status = root.querySelector('[data-size-advisor-status]');
    const resultCard = root.querySelector('[data-size-advisor-result]');
    const resultGuide = root.querySelector('[data-size-advisor-guide]');
    const resultHeading = root.querySelector('[data-size-advisor-result-size]');
    const resultCm = root.querySelector('[data-size-advisor-result-cm]');
    const resultMessage = root.querySelector('[data-size-advisor-result-message]');
    const debugNode = root.querySelector('[data-size-advisor-debug]');

    const bindOpenButtons = () => {
      const interactionScope = root.closest('[id^="shopify-section-"]') || document;
      interactionScope.querySelectorAll('[data-open-size-service]').forEach((button) => {
        if (button.dataset.sizeAdvisorBound === 'true') return;
        button.dataset.sizeAdvisorBound = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          dialogComponent?.showDialog?.();
        });
      });
    };

    bindOpenButtons();

    if (!canvas || !context || !uploadInput) return;

    const params = new URLSearchParams(window.location.search);
    const sampleKey = window.location.protocol === 'file:' ? params.get('sample') : null;
    const sampleAsset = sampleKey && SAMPLE_FILES[sampleKey]
      ? new URL(SAMPLE_FILES[sampleKey], window.location.href).href
      : null;

    const state = {
      stage: 'upload',
      image: null,
      imageUrl: '',
      card: createInitialCardRect(),
      line: createInitialPalmLine(),
      drag: null,
      result: null,
      debugPayload: null,
      zoom: window.matchMedia('(min-width: 990px)').matches ? 1.45 : 1.12,
    };

    const updateZoomUi = () => {
      const percent = `${Math.round(state.zoom * 100)}%`;
      if (zoomDisplay) zoomDisplay.textContent = percent;
      if (zoomResetButton) zoomResetButton.setAttribute('aria-label', `Reset zoom to 100%, current zoom ${percent}`);
    };

    const withZoomPreservedViewport = (callback) => {
      const shell = canvasShell;
      const previousWidth = canvas.width || 1;
      const previousHeight = canvas.height || 1;
      const centerX = shell ? (shell.scrollLeft + shell.clientWidth / 2) / previousWidth : 0.5;
      const centerY = shell ? (shell.scrollTop + shell.clientHeight / 2) / previousHeight : 0.5;
      callback();
      draw();
      if (!shell) return;
      shell.scrollLeft = Math.max(0, canvas.width * centerX - shell.clientWidth / 2);
      shell.scrollTop = Math.max(0, canvas.height * centerY - shell.clientHeight / 2);
    };

    const setZoom = (nextZoom) => {
      const bounded = clamp(Number(nextZoom.toFixed(2)), MIN_ZOOM, MAX_ZOOM);
      if (Math.abs(bounded - state.zoom) < 0.001) return;
      withZoomPreservedViewport(() => {
        state.zoom = bounded;
        updateZoomUi();
      });
    };

    const setTab = (target) => {
      const isChart = target === 'chart';
      chartTab?.classList.toggle('is-active', isChart);
      findTab?.classList.toggle('is-active', !isChart);
      chartTab?.setAttribute('aria-selected', isChart ? 'true' : 'false');
      findTab?.setAttribute('aria-selected', !isChart ? 'true' : 'false');
      if (chartPanel) chartPanel.hidden = !isChart;
      if (findPanel) findPanel.hidden = isChart;
    };

    const renderDebug = (payload) => {
      if (!SIZE_ADVISOR_DEBUG || !debugNode) return;
      state.debugPayload = payload;
      debugNode.hidden = false;
      debugNode.textContent = Object.entries(payload)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
    };

    const clearDebug = () => {
      if (!debugNode) return;
      state.debugPayload = null;
      debugNode.hidden = true;
      debugNode.textContent = '';
    };

    const updatePrompt = () => {
      if (!promptTitle || !promptCopy) return;
      if (state.stage === 'upload') {
        promptTitle.textContent = 'Upload a photo of your hand placed flat next to a standard card.';
        promptCopy.textContent = 'Keep the full hand and the full card visible. For privacy, avoid showing card numbers or personal details.';
      } else if (state.stage === 'card') {
        promptTitle.textContent = 'Align the rectangle with the card.';
        promptCopy.textContent = 'Drag the box and its corners until it follows the full card closely. Use a plain card when possible, or cover sensitive data before taking the photo.';
      } else if (state.stage === 'palm') {
        promptTitle.textContent = 'Place the line across the palm, excluding the thumb.';
        promptCopy.textContent = 'Place it across the palm just below the fingers, as shown in the guide. Stop before the thumb: the thumb must not be included.';
      } else {
        promptTitle.textContent = 'Recommended size ready.';
        promptCopy.textContent = 'You can still adjust the card or palm line, then calculate again if you want to refine it.';
      }
    };

    const updateControls = () => {
      const hasImage = Boolean(state.image);
      if (canvasShell) canvasShell.hidden = !hasImage;
      if (precisionTools) precisionTools.hidden = !hasImage;
      if (uploadButton) uploadButton.hidden = hasImage;
      if (confirmCardButton) confirmCardButton.hidden = !(hasImage && state.stage === 'card');
      if (backToCardButton) backToCardButton.hidden = !(hasImage && (state.stage === 'palm' || state.stage === 'result'));
      if (calculateButton) calculateButton.hidden = !(hasImage && (state.stage === 'palm' || state.stage === 'result'));
      if (resetButton) resetButton.hidden = !hasImage;
      if (resultCard) resultCard.hidden = !state.result;
      if (resultGuide) resultGuide.hidden = hasImage || Boolean(state.result);
      updatePrompt();
    };

    const draw = () => {
      const image = state.image;
      if (!image) {
        canvas.width = 1;
        canvas.height = 1;
        context.clearRect(0, 0, 1, 1);
        return;
      }

      const shellWidth = canvas.parentElement?.clientWidth || 700;
      const baseWidth = Math.max(360, Math.min(shellWidth - 2, 760));
      const width = Math.round(baseWidth * state.zoom);
      const height = Math.round(width * (image.naturalHeight / image.naturalWidth));
      canvas.width = width;
      canvas.height = height;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      const card = {
        x: state.card.x * width,
        y: state.card.y * height,
        w: state.card.w * width,
        h: state.card.h * height,
      };
      const line = {
        a: { x: state.line.x1 * width, y: state.line.y1 * height },
        b: { x: state.line.x2 * width, y: state.line.y2 * height },
      };

      context.lineWidth = 3;
      context.strokeStyle = '#cb9152';
      context.fillStyle = 'rgba(203, 145, 82, 0.08)';
      const cardBox = normalizedRect(card);
      context.fillRect(cardBox.x, cardBox.y, cardBox.w, cardBox.h);
      context.strokeRect(cardBox.x, cardBox.y, cardBox.w, cardBox.h);

      if (state.stage === 'card' || state.stage === 'palm' || state.stage === 'result') {
        Object.values(cardHandles(cardBox)).forEach((handle) => drawCardHandle(context, handle));
      }

      if (state.stage === 'palm' || state.stage === 'result') {
        context.strokeStyle = '#8a7654';
        context.lineWidth = 4;
        context.beginPath();
        context.moveTo(line.a.x, line.a.y);
        context.lineTo(line.b.x, line.b.y);
        context.stroke();
        const angle = Math.atan2(line.b.y - line.a.y, line.b.x - line.a.x);
        drawPalmCap(context, line.a, angle);
        drawPalmCap(context, line.b, angle);
      }
    };

    const loadImage = (src) => {
      const image = new Image();
      image.onload = () => {
        state.image = image;
        state.imageUrl = src;
        state.card = createInitialCardRect();
        state.line = createInitialPalmLine();
        state.stage = 'card';
        state.result = null;
        state.zoom = window.matchMedia('(min-width: 990px)').matches ? 1.45 : 1.12;
        if (status) status.textContent = 'Align the rectangle to the card, then confirm it.';
        clearDebug();
        updateZoomUi();
        updateControls();
        draw();
      };
      image.onerror = () => {
        if (status) status.textContent = 'We couldn’t load the image. Please try another photo.';
      };
      image.src = src;
    };

    const resetAdvisor = () => {
      state.stage = 'upload';
      state.image = null;
      state.imageUrl = '';
      state.card = createInitialCardRect();
      state.line = createInitialPalmLine();
      state.drag = null;
      state.result = null;
      state.zoom = window.matchMedia('(min-width: 990px)').matches ? 1.45 : 1.12;
      uploadInput.value = '';
      if (status) status.textContent = '';
      clearDebug();
      updateZoomUi();
      updateControls();
      draw();
    };

    const validateCard = () => {
      const width = canvas.width;
      const height = canvas.height;
      const card = normalizedRect({
        x: state.card.x * width,
        y: state.card.y * height,
        w: state.card.w * width,
        h: state.card.h * height,
      });
      if (card.w < MIN_CARD_SIDE_PX || card.h < MIN_CARD_SIDE_PX) {
        if (status) status.textContent = 'Please make the card rectangle larger and align it with the card.';
        return false;
      }
      return true;
    };

    const validatePalmLine = () => {
      const width = canvas.width;
      const height = canvas.height;
      const a = { x: state.line.x1 * width, y: state.line.y1 * height };
      const b = { x: state.line.x2 * width, y: state.line.y2 * height };
      if (distance(a, b) < MIN_PALM_LINE_PX) {
        if (status) status.textContent = 'Please place the line across the widest part of your palm.';
        return false;
      }
      return true;
    };

    const calculate = () => {
      if (!validateCard() || !validatePalmLine()) return;

      const width = canvas.width;
      const height = canvas.height;
      const card = normalizedRect({
        x: state.card.x * width,
        y: state.card.y * height,
        w: state.card.w * width,
        h: state.card.h * height,
      });
      const cardWidthPx = card.w;
      const cardHeightPx = card.h;
      const longSidePx = Math.max(cardWidthPx, cardHeightPx);
      const shortSidePx = Math.min(cardWidthPx, cardHeightPx);
      const pxPerMmLong = longSidePx / STANDARD_CARD_LONG_MM;
      const pxPerMmShort = shortSidePx / STANDARD_CARD_SHORT_MM;
      const pxPerMm = (pxPerMmLong + pxPerMmShort) / 2;

      const a = { x: state.line.x1 * width, y: state.line.y1 * height };
      const b = { x: state.line.x2 * width, y: state.line.y2 * height };
      const palmWidthPx = distance(a, b);
      const palmWidthMm = palmWidthPx / pxPerMm;
      const palmWidthCm = palmWidthMm / 10;
      const palmCircumferenceCm = palmWidthCm * PALM_WIDTH_TO_CIRCUMFERENCE_FACTOR;
      const result = recommendSize(palmCircumferenceCm, fitType);

      state.stage = 'result';
      state.result = result;
      resultHeading.textContent = `Recommended size: ${result.recommendedSize}`;
      resultCm.textContent = `${result.estimatedCm.toFixed(1)} cm / ${result.estimatedIn.toFixed(1)} in`;
      resultMessage.textContent = result.message;

      const debugPayload = {
        cardWidthPx: cardWidthPx.toFixed(2),
        cardHeightPx: cardHeightPx.toFixed(2),
        pxPerMmLong: pxPerMmLong.toFixed(4),
        pxPerMmShort: pxPerMmShort.toFixed(4),
        pxPerMm: pxPerMm.toFixed(4),
        palmWidthPx: palmWidthPx.toFixed(2),
        palmWidthCm: palmWidthCm.toFixed(2),
        palmCircumferenceCm: palmCircumferenceCm.toFixed(2),
        recommendedSize: result.recommendedSize,
        selectedVariant: 'not selected yet',
      };

      renderDebug(debugPayload);
      debugLog(debugPayload);
      if (status) status.textContent = 'Estimate ready. Use the suggested size as your starting point.';
      updateControls();
      draw();
      const scrollHost = root.closest('.size-advisor') || resultCard?.parentElement;
      if (scrollHost && resultCard) {
        const targetTop = Math.max(0, resultCard.offsetTop - 24);
        scrollHost.scrollTo({ top: targetTop, behavior: 'smooth' });
      } else {
        resultCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const setDragForCard = (point) => {
      const width = canvas.width;
      const height = canvas.height;
      const card = normalizedRect({
        x: state.card.x * width,
        y: state.card.y * height,
        w: state.card.w * width,
        h: state.card.h * height,
      });
      const handles = cardHandles(card);
      for (const [name, handle] of Object.entries(handles)) {
        if (distance(point, handle) <= HANDLE_HIT_RADIUS) {
          state.drag = {
            type: 'card-handle',
            handle: name,
          };
          return true;
        }
      }
      if (pointInRect(point, card)) {
        state.drag = {
          type: 'card-move',
          offsetX: point.x - card.x,
          offsetY: point.y - card.y,
          width: card.w,
          height: card.h,
        };
        return true;
      }
      return false;
    };

    const setDragForLine = (point) => {
      const width = canvas.width;
      const height = canvas.height;
      const a = { x: state.line.x1 * width, y: state.line.y1 * height };
      const b = { x: state.line.x2 * width, y: state.line.y2 * height };
      if (distance(point, a) <= HANDLE_HIT_RADIUS) {
        state.drag = { type: 'line-end', end: 'a' };
        return true;
      }
      if (distance(point, b) <= HANDLE_HIT_RADIUS) {
        state.drag = { type: 'line-end', end: 'b' };
        return true;
      }
      if (segmentDistance(point, a, b) <= LINE_HIT_RADIUS) {
        state.drag = {
          type: 'line-move',
          lastPoint: point,
        };
        return true;
      }
      return false;
    };

    const onPointerDown = (event) => {
      if (!state.image) return;
      const point = getEventPoint(event, canvas);
      let grabbed = false;
      if (state.stage === 'card') {
        grabbed = setDragForCard(point);
      } else if (state.stage === 'palm' || state.stage === 'result') {
        grabbed = setDragForLine(point) || setDragForCard(point);
      }
      if (grabbed) {
        if (typeof canvas.setPointerCapture === 'function' && Number.isFinite(event.pointerId)) {
          try {
            canvas.setPointerCapture(event.pointerId);
          } catch (error) {
            void error;
          }
        }
        event.preventDefault();
      } else if (canvasShell && state.zoom > 1.01) {
        state.drag = {
          type: 'pan-canvas',
          startClientX: event.clientX,
          startClientY: event.clientY,
          startScrollLeft: canvasShell.scrollLeft,
          startScrollTop: canvasShell.scrollTop,
        };
        event.preventDefault();
      }
    };

    const onPointerMove = (event) => {
      if (!state.drag || !state.image) return;
      const width = canvas.width;
      const height = canvas.height;
      const point = getEventPoint(event, canvas);

      if (state.drag.type === 'pan-canvas') {
        if (canvasShell) {
          canvasShell.scrollLeft = state.drag.startScrollLeft - (event.clientX - state.drag.startClientX);
          canvasShell.scrollTop = state.drag.startScrollTop - (event.clientY - state.drag.startClientY);
        }
      } else if (state.drag.type === 'card-move') {
        const nextX = clamp((point.x - state.drag.offsetX) / width, 0, 1 - state.drag.width / width);
        const nextY = clamp((point.y - state.drag.offsetY) / height, 0, 1 - state.drag.height / height);
        state.card = {
          x: nextX,
          y: nextY,
          w: state.drag.width / width,
          h: state.drag.height / height,
        };
      } else if (state.drag.type === 'card-handle') {
        const current = normalizedRect({
          x: state.card.x * width,
          y: state.card.y * height,
          w: state.card.w * width,
          h: state.card.h * height,
        });
        let left = current.x;
        let top = current.y;
        let right = current.x + current.w;
        let bottom = current.y + current.h;

        if (state.drag.handle.includes('n')) top = clamp(point.y, 0, bottom - MIN_CARD_SIDE_PX);
        if (state.drag.handle.includes('s')) bottom = clamp(point.y, top + MIN_CARD_SIDE_PX, height);
        if (state.drag.handle.includes('w')) left = clamp(point.x, 0, right - MIN_CARD_SIDE_PX);
        if (state.drag.handle.includes('e')) right = clamp(point.x, left + MIN_CARD_SIDE_PX, width);

        state.card = {
          x: left / width,
          y: top / height,
          w: (right - left) / width,
          h: (bottom - top) / height,
        };
      } else if (state.drag.type === 'line-end') {
        const clampedX = clamp(point.x / width, 0, 1);
        const clampedY = clamp(point.y / height, 0, 1);
        if (state.drag.end === 'a') {
          state.line.x1 = clampedX;
          state.line.y1 = clampedY;
        } else {
          state.line.x2 = clampedX;
          state.line.y2 = clampedY;
        }
      } else if (state.drag.type === 'line-move') {
        const deltaX = (point.x - state.drag.lastPoint.x) / width;
        const deltaY = (point.y - state.drag.lastPoint.y) / height;
        const next = {
          x1: clamp(state.line.x1 + deltaX, 0, 1),
          y1: clamp(state.line.y1 + deltaY, 0, 1),
          x2: clamp(state.line.x2 + deltaX, 0, 1),
          y2: clamp(state.line.y2 + deltaY, 0, 1),
        };
        const adjustX = Math.min(0, next.x1, next.x2) + Math.max(0, next.x1 - 1, next.x2 - 1);
        const adjustY = Math.min(0, next.y1, next.y2) + Math.max(0, next.y1 - 1, next.y2 - 1);
        state.line = {
          x1: next.x1 - adjustX,
          y1: next.y1 - adjustY,
          x2: next.x2 - adjustX,
          y2: next.y2 - adjustY,
        };
        state.drag.lastPoint = point;
      }

      event.preventDefault();
      if (state.drag.type !== 'pan-canvas') draw();
    };

    const onPointerUp = (event) => {
      if (!state.drag) return;
      state.drag = null;
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch (error) {
        void error;
      }
    };

    const loadFromSample = () => {
      if (!sampleAsset || state.image) return;
      loadImage(sampleAsset);
      setTab('find');
    };

    const makePointerLikeEvent = (originalEvent, point) => ({
      clientX: point.x,
      clientY: point.y,
      pointerId: 1,
      preventDefault: () => originalEvent.preventDefault(),
    });

    const getTouchPoint = (event) => {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      if (!touch) return null;
      return {
        x: touch.clientX,
        y: touch.clientY,
      };
    };

    chartTab?.addEventListener('click', () => setTab('chart'));
    findTab?.addEventListener('click', () => {
      setTab('find');
      if (sampleAsset && !state.image) loadFromSample();
    });

    const loadChosenFile = (fileInput) => {
      const [file] = fileInput.files || [];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => loadImage(String(reader.result));
      reader.onerror = () => {
        if (status) status.textContent = 'We couldn’t load the image. Please try another photo.';
      };
      reader.readAsDataURL(file);
    };

    uploadButton?.addEventListener('click', () => uploadInput.click());
    uploadInput.addEventListener('change', () => loadChosenFile(uploadInput));

    confirmCardButton?.addEventListener('click', () => {
      if (!validateCard()) return;
      state.stage = 'palm';
      if (status) status.textContent = 'Now place the line across the palm just below the fingers, excluding the thumb.';
      updateControls();
      draw();
    });

    backToCardButton?.addEventListener('click', () => {
      state.stage = 'card';
      if (status) status.textContent = 'Adjust the card rectangle, then confirm it again.';
      updateControls();
      draw();
    });

    calculateButton?.addEventListener('click', calculate);
    resetButton?.addEventListener('click', resetAdvisor);
    zoomOutButton?.addEventListener('click', () => setZoom(state.zoom - 0.2));
    zoomInButton?.addEventListener('click', () => setZoom(state.zoom + 0.2));
    zoomResetButton?.addEventListener('click', () => setZoom(1));

    if ('PointerEvent' in window) {
      canvas.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    } else {
      canvas.addEventListener('mousedown', (event) => {
        onPointerDown(makePointerLikeEvent(event, { x: event.clientX, y: event.clientY }));
      });
      window.addEventListener('mousemove', (event) => {
        if (!state.drag) return;
        onPointerMove(makePointerLikeEvent(event, { x: event.clientX, y: event.clientY }));
      });
      window.addEventListener('mouseup', (event) => {
        if (!state.drag) return;
        onPointerUp(makePointerLikeEvent(event, { x: event.clientX, y: event.clientY }));
      });
      canvas.addEventListener('touchstart', (event) => {
        const point = getTouchPoint(event);
        if (!point) return;
        onPointerDown(makePointerLikeEvent(event, point));
      }, { passive: false });
      window.addEventListener('touchmove', (event) => {
        if (!state.drag) return;
        const point = getTouchPoint(event);
        if (!point) return;
        onPointerMove(makePointerLikeEvent(event, point));
      }, { passive: false });
      window.addEventListener('touchend', (event) => {
        if (!state.drag) return;
        const point = getTouchPoint(event) || { x: 0, y: 0 };
        onPointerUp(makePointerLikeEvent(event, point));
      }, { passive: false });
    }

    dialogComponent?.addEventListener('dialog:close', resetAdvisor);
    window.addEventListener('pageshow', resetAdvisor, { once: true });
    window.addEventListener('resize', draw);

    if (status) status.textContent = '';
    updateZoomUi();
    updateControls();
    setTab('chart');

    if (sampleAsset) {
      requestAnimationFrame(() => {
        loadFromSample();
      });
    }
  };

  const bootSizeAdvisors = () => {
    document.querySelectorAll('[data-size-advisor]').forEach((root) => {
      try {
        initSizeAdvisor(root);
      } catch (error) {
        console.error('Size advisor failed to initialise.', error);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSizeAdvisors);
  } else {
    bootSizeAdvisors();
  }
})();
